import { createFileRoute, useRouter } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { Card, CardTitle } from "@/components/ui/card.tsx";
import { Trans } from "@lingui/react/macro";
import { Separator } from "@/components/ui/separator.tsx";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useCreateUserMutation, USERS_KEY } from "@/modules/users/api/useUsers.ts";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { logError } from "@/sentry.ts";

export const Route = createFileRoute("/settings/users/new")({
  component: RouteComponent,
});

type Inputs = { firstName: string; lastName: string; email: string; phoneNumber: string };

function RouteComponent() {
  const title = t`New user`;
  const { activeApp } = useAppConfig();

  const form = useForm<Inputs>({
    defaultValues: { phoneNumber: "", email: "", firstName: "", lastName: "" },
  });
  const isDirty = form.formState.isDirty;

  const { mutateAsync } = useCreateUserMutation({ merchantId: activeApp });
  const queryClient = useQueryClient();
  const { navigate } = useRouter();

  const onSubmit = async ({ firstName, lastName, email, phoneNumber }: Inputs) => {
    try {
      await mutateAsync({ firstName, lastName, email, phoneNumber, role: "admin" });
      await queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
      toast.success(t`User was added successfully.`);
      void navigate({ to: "/settings/users" });
    } catch (e) {
      logError(e);
      toast.error(t`Something went wrong. We were unable to add user. Please try again.`);
    }
  };

  return (
    <PageWrapper>
      <PageHeader
        breadcrumbs={[
          { href: "/settings/users/", title: t`Users administration` },
          { href: "/", title },
        ]}
      />
      <div className="flex flex-col grow p-5">
        <Card className={"shadow-none"}>
          <div className={"px-5"}>
            <CardTitle className={"text-xl font-semibold"}>
              <Trans>Basic information</Trans>
            </CardTitle>
          </div>
          <Separator />

          <Form {...form}>
            <form className={"px-5 w-full flex flex-col"} onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                rules={{ required: t`This field is required` }}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans>First name*</Trans>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={"Jan"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                rules={{ required: t`This field is required` }}
                name="lastName"
                render={({ field }) => (
                  <FormItem className={"mt-3"}>
                    <FormLabel>
                      <Trans>Last name*</Trans>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={"Novák"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                rules={{ required: t`This field is required` }}
                render={({ field }) => (
                  <FormItem className={"mt-3"}>
                    <FormLabel>
                      <Trans>E-mail*</Trans>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={"mail@mail.com"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className={"mt-3"}>
                    <FormLabel>
                      <Trans>Phone number</Trans>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={"+420 123 456 789"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className={"flex gap-4 ml-auto"}>
                <Button
                  type={"button"}
                  className={"mt-3"}
                  variant={"secondary"}
                  onClick={() => navigate({ to: "/settings/users" })}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button type={"submit"} className={"mt-3"} disabled={!isDirty}>
                  <Trans>Save</Trans>
                </Button>
              </div>
            </form>
          </Form>
        </Card>
      </div>
    </PageWrapper>
  );
}
